"use client";

/**
 * DiscoverySpotlightsList 컴포넌트
 * @description Post Spotlights 의 목록 — 머리줄 · 카드 격자 · 더 불러오기. 앱의 무한 스크롤
 *   자리를 웹은 버튼으로 받는다(데스크톱 시안의 "Load more"). 오류와 재시도는 시안에 없어도
 *   유지한다 — 빠지면 결함이다.
 */
import { Button } from "@fanwork-web/design-system";
import { memo, useCallback } from "react";

import { discoverySpotlightsStyles } from "./DiscoverySpotlights.styles";
import type {
  DiscoverySpotlightsListProps,
  SpotlightItem,
} from "./DiscoverySpotlights.types";
import DiscoverySpotlightsCard from "./DiscoverySpotlightsCard";
import { useDiscoverySpotlights } from "./useDiscoverySpotlights";

import { COMMON_COPY, EXPLORE_COPY } from "../../constants/copy";
import { MasonryList, Reveal, Section, SectionHeader } from "@/shared/ui";

/** 주소가 곧 신원이다 — 아티클과 앱 글이 한 주소 공간을 나눠 쓰므로 겹치지 않는다 */
const keyExtractor = (item: SpotlightItem): string => item.slug;

const DiscoverySpotlightsList = memo<DiscoverySpotlightsListProps>(
  ({ initialPage }) => {
    const { items, hasNextPage, isLoading, isError, loadMore } =
      useDiscoverySpotlights(initialPage);

    const renderItem = useCallback(
      (item: SpotlightItem, index: number) => (
        <DiscoverySpotlightsCard item={item} index={index} />
      ),
      [],
    );

    const footer = isError ? (
      <div className={discoverySpotlightsStyles.error}>
        <p className={discoverySpotlightsStyles.state}>
          {COMMON_COPY.error.generic}
        </p>
        <Button
          text={COMMON_COPY.button.retry}
          tone="outlined"
          size="sm"
          radius="circle"
          onClick={loadMore}
        />
      </div>
    ) : hasNextPage ? (
      <Button
        text={COMMON_COPY.button.loadMore}
        tone="outlined"
        radius="circle"
        disabled={isLoading}
        onClick={loadMore}
      />
    ) : null;

    return (
      <MasonryList
        inset="tab"
        data={items}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        header={
          <Section padding="sm">
            <Reveal>
              <SectionHeader title={EXPLORE_COPY.postSpotlights.title} />
            </Reveal>
          </Section>
        }
        /* 아직 오는 중이거나 오류면 "없다"고 말하지 않는다 */
        empty={
          isLoading || isError ? undefined : (
            <p className={discoverySpotlightsStyles.state}>
              {COMMON_COPY.error.notFound}
            </p>
          )
        }
        footer={footer}
      />
    );
  },
);

DiscoverySpotlightsList.displayName = "DiscoverySpotlightsList";

export default DiscoverySpotlightsList;
